import { SITE } from "./site";

export type RouteSEO = {
  title: string;
  description: string;
  type?: "website" | "article";
  image?: string;
  noIndex?: boolean;
  keywords?: string[];
  jsonLd?: Record<string, any>[];
};

export const ROUTE_SEO: Record<string, RouteSEO> = {
  "/": {
    title: "Unified AI Platform for Hiring | Babblebots AI",
    description:
      "Babblebots AI is a unified hiring platform for AI-first teams. Automate sourcing, screening, interviews, and shortlisting without compromising evaluation quality.",
    type: "website",
    image: SITE.ogDefault,
    keywords: [
      "AI hiring platform",
      "AI recruiter",
      "recruitment automation",
      "AI screening",
      "AI interviews",
      "shortlisting automation",
      "talent acquisition software",
    ],
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: SITE.name,
        url: SITE.domain,
        logo: SITE.logo,
        sameAs: ["https://www.linkedin.com/company/babblebots"],
      },
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: SITE.name,
        url: SITE.domain,
      },
    ],
  },

  "/resources": {
    title: "Hiring Resources & Insights | Babblebots AI",
    description:
      "Explore AI hiring resources including customer case studies, product updates, reports, guides, podcasts, and expert insights.",
    image: SITE.ogDefault,
    keywords: [
      "recruitment resources",
      "AI recruiting guides",
      "hiring insights",
      "talent acquisition resources",
      "AI hiring reports",
    ],
  },

  "/customers": {
    title: "Customer Success Stories | Babblebots AI",
    description:
      "Real customer success stories showing how teams use Babblebots AI to reduce time-to-hire, improve screening quality, and scale recruiting.",
    image: SITE.ogDefault,
    keywords: [
      "customer success stories",
      "AI recruiting case studies",
      "Babblebots customers",
      "hiring automation results",
      "AI recruitment success",
    ],
  },

  "/pricing": {
    title: "Pricing & Plans | Babblebots AI",
    description:
      "Simple, credit-based pricing for Babblebots AI. One credit system covers AI sourcing, screening, and interviews — no separate modules to buy.",
    keywords: [
      "Babblebots pricing",
      "AI recruitment pricing",
      "hiring automation cost",
      "AI interview pricing",
      "credit based hiring pricing",
      "AI sourcing and screening pricing",
    ],
  },

  "/api": {
    title: "Developer API | Babblebots AI",
    description:
      "Integrate AI-powered interviews, phone screening, and assessments into your platform with the Babblebots API. RESTful, secure, and multilingual.",
    image: SITE.ogDefault,
    keywords: [
      "Babblebots API",
      "AI interview API",
      "hiring API",
      "recruitment API",
      "AI screening API",
      "developer hiring tools",
      "AI assessment API",
    ],
  },

  "/ai-talent-drive": {
    title: "AI Jobs Drive | Multi-Company AI Interview Drive | Babblebots",
    description:
      "Register once and get considered by 50+ hiring companies across AI-first roles: Forward Deployed Engineer, AI Engineer, Full Stack, Backend and Frontend Engineer, AI Product Manager, GTM Engineer, Software / AI Engineering Intern and Data Engineer.",
    type: "website",
    image: SITE.ogDefault,
    keywords: [
      "AI jobs drive",
      "AI talent drive",
      "multi company hiring drive",
      "AI interview registration",
      "forward deployed engineer jobs India",
      "gen AI engineer jobs India",
      "full stack developer hiring",
      "backend developer hiring India",
      "frontend developer hiring India",
      "product manager jobs India",
    ],
  },

  "/ai-talent-drive/hiring-companies": {
    title: "Hire from the AI Talent Drive | Free Company Sign-up | Babblebots",
    description:
      "Sign up free to hire AI-vetted candidates from India's largest AI Talent Drive. Every candidate is interviewed by AI, across nine role tracks from AI Engineer and Data Engineer to AI Product Manager and interns.",
    type: "website",
    image: SITE.ogDefault,
    keywords: [
      "hire AI engineers India",
      "AI talent drive for companies",
      "AI-vetted candidates",
      "hire gen AI engineers",
      "virtual hiring drive India",
    ],
  },

  "/contactus": {
    title: "Contact Sales | Babblebots AI",
    description:
      "Talk to the Babblebots team to see how AI-powered recruiting can transform your hiring process.",
    keywords: [
      "contact Babblebots",
      "AI recruiting demo",
      "hiring automation consultation",
      "talk to sales",
    ],
  },

  "/book-demo": {
    title: "Book a Demo | Babblebots AI",
    description:
      "Schedule a live demo of Babblebots AI and see how AI-powered sourcing, screening, and interviews can cut your time-to-hire.",
    keywords: [
      "book a demo",
      "AI recruiting demo",
      "hiring automation demo",
      "schedule a call Babblebots",
    ],
  },

  "/blog": {
    title: "Babblebots Blog | AI Hiring Insights",
    description:
      "Insights on AI recruiting, hiring strategy, case studies, and product updates from the Babblebots team.",
    type: "website",
    image: SITE.ogDefault,
    keywords: [
      "AI recruiting blog",
      "recruitment strategy",
      "hiring best practices",
      "talent acquisition blog",
      "AI hiring insights",
    ],
  },

  "/termsandconditions": {
    title: "Terms & Conditions | Babblebots",
    description:
      "Terms and conditions governing the use of Babblebots products and services.",
    noIndex: false,
    keywords: ["terms", "terms and conditions", "Babblebots terms"],
  },

  "/privacy-policy": {
    title: "Privacy Policy | Babblebots",
    description:
      "Learn how Babblebots collects, uses, and protects your data.",
    noIndex: false,
    keywords: ["privacy policy", "data protection", "Babblebots privacy"],
  },

  "/mtw": {
    title: "Mumbai Tech Week AI Job Fair 2026 | Babblebots AI",
    description:
      "India's first Agentic-AI led job fair. See how Babblebots powered Mumbai Tech Week 2026: 26,000+ candidates screened, 6,500+ AI interviews, and 700+ in-person interviews across 20+ companies.",
    image: SITE.ogDefault,
    keywords: [
      "Mumbai Tech Week",
      "AI job fair",
      "agentic AI hiring event",
      "AI recruitment event",
      "AI campus drive",
      "Babblebots MTW",
    ],
  },

  // Old per-module pricing pages (ai-screening, ai-talent-discovery,
  // hiring-events, developer-platform) were retired 2026-08-30 when pricing
  // consolidated into the single credit-based "/pricing" page above. Their
  // URLs still resolve — src/pages/Pricing.tsx client-redirects each to
  // "/pricing" — so intentionally no ROUTE_SEO entries remain for them here;
  // once redirected, the page picks up "/pricing"'s title/canonical instead.
};

// Normalize paths (no trailing slash except root)
export function normalizePath(pathname: string) {
  if (!pathname || pathname === "/") return "/";
  return pathname.replace(/\/+$/, "");
}
